/**
 * SalesTeamFunnelReportModal Component
 *
 * Custom modal for the multi-location Sales Team Report: a location
 * multi-select (default all vehicle rooftops) plus a report WINDOW that is
 * either a single date (the last 5 working days ending there — the default,
 * and what the report has always shown) or a date range with weekly trend
 * columns.
 *
 * Parameter state and validation live in funnel/salesTeamFunnelParams.ts, the
 * form in funnel/SalesTeamFunnelParamsForm.tsx, and the exports in
 * funnel/salesTeamFunnelExport.ts, so this file keeps only the status machine,
 * the fetch/retry logic, and the layout.
 */

import React, { useMemo, useState } from 'react';
import { XMarkIcon, DocumentChartBarIcon } from '@heroicons/react/24/outline';
import { ReportDefinition } from '../../types/reports';
import { ReportExportButtons } from './ReportExportButtons';
import {
  RunReportResponse,
  runSalesTeamFunnelReport,
  runSalesTeamFunnelReportRange,
} from '../../services/api';
import { SalesTeamFunnelReport } from './renderers/SalesTeamFunnelReport';
import { SalesTeamFunnelReportData } from '../../types/salesTeamFunnel';
import { formatLongDate, formatReportDate } from '../../utils/localDate';
import {
  FunnelReportParams,
  defaultFunnelParams,
  selectedIds,
  validateFunnelParams,
} from './funnel/salesTeamFunnelParams';
import { SalesTeamFunnelParamsForm } from './funnel/SalesTeamFunnelParamsForm';
import { exportRangeWorkbook, exportRecentCsv } from './funnel/salesTeamFunnelExport';

interface SalesTeamFunnelReportModalProps {
  report: ReportDefinition;
  isOpen: boolean;
  onClose: () => void;
}

type ReportStatus = 'idle' | 'running' | 'complete' | 'error';

/** Print scoping class — see the @media print block in index.css. */
const PRINT_BODY_CLASS = 'printing-funnel-report';

export const SalesTeamFunnelReportModal: React.FC<SalesTeamFunnelReportModalProps> = ({
  report,
  isOpen,
  onClose,
}) => {
  // Every allowed role — DepartmentManager included — may run the report for
  // any rooftop (Kim, 2026-08-31): the funnel exposes only lead/visit/appt/
  // unit counts, and NEW-dept managers compare against every location.
  const [params, setParams] = useState<FunnelReportParams>(defaultFunnelParams);

  const [status, setStatus] = useState<ReportStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<RunReportResponse | null>(null);
  const [runningMessage, setRunningMessage] = useState('Generating report...');
  const [isExporting, setIsExporting] = useState(false);

  const validation = useMemo(() => validateFunnelParams(params), [params]);

  /**
   * Any parameter change discards rendered results. A 16-week table sitting
   * under a toggle that reads "Last 5 working days" would be a lie the CEO
   * could act on.
   */
  const handleParamsChange = (next: FunnelReportParams) => {
    setParams(next);
    if (status !== 'running') {
      setStatus('idle');
      setReportData(null);
      setError(null);
    }
  };

  const isRetryableError = (err: unknown): boolean => {
    const msg = err instanceof Error ? err.message.toLowerCase() : '';
    return msg.includes('failed to fetch') || msg.includes('networkerror') ||
      msg.includes('timeout') || msg.includes('504') || msg.includes('503') ||
      msg.includes('network') || msg.includes('aborted') || msg.includes('timed out');
  };

  const handleRunReport = async () => {
    if (!validation.canRun) return;

    const locationIds = selectedIds(params);
    const requestedStart = params.mode === 'range' ? params.startDate : null;

    setStatus('running');
    setError(null);
    setReportData(null);
    setRunningMessage('Generating report...');

    const warmupTimer = setTimeout(() => {
      setRunningMessage('Warming up connection...');
    }, 5000);

    const MAX_ATTEMPTS = 2;
    let lastError: unknown;

    try {
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const response = params.mode === 'range'
            ? await runSalesTeamFunnelReportRange(
              report.id, locationIds, params.startDate, params.endDate,
            )
            : await runSalesTeamFunnelReport(report.id, locationIds, params.reportDate);

          if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to generate report');
          }

          clearTimeout(warmupTimer);
          setReportData(response.data);
          // If the backend clamped the range, say so — otherwise the operator
          // would silently be looking at a different window than they picked.
          const returned = (response.data.data as SalesTeamFunnelReportData)?.rangeStartDate;
          if (requestedStart && returned && returned !== requestedStart) {
            setError(`Range clamped to available history: ${formatLongDate(returned)} onward.`);
          }
          setStatus('complete');
          return;
        } catch (err) {
          lastError = err;
          if (attempt < MAX_ATTEMPTS && isRetryableError(err)) {
            console.warn(`Report attempt ${attempt} failed, retrying...`, err);
            setRunningMessage('Retrying...');
            continue;
          }
          throw err;
        }
      }
    } catch {
      clearTimeout(warmupTimer);
      setError(lastError instanceof Error ? lastError.message : 'Failed to generate report');
      setStatus('error');
    }
  };

  /**
   * Scope the print to the results pane. Follows the body.printing-nut-sheet
   * pattern; the setTimeout fallback is there because Safari does not reliably
   * fire `afterprint`.
   */
  const handlePrintPDF = () => {
    document.body.classList.add(PRINT_BODY_CLASS);
    const cleanup = () => document.body.classList.remove(PRINT_BODY_CLASS);
    window.addEventListener('afterprint', cleanup, { once: true });
    window.print();
    setTimeout(cleanup, 1500);
  };

  const handleExport = async () => {
    const data = reportData?.data as SalesTeamFunnelReportData | undefined;
    if (!data) return;

    setIsExporting(true);
    try {
      if (data.periods && data.rangeStartDate && data.rangeEndDate) {
        await exportRangeWorkbook(data);
      } else {
        exportRecentCsv(data);
      }
    } catch (err) {
      console.error('Sales Team Report export failed', err);
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const handleClose = () => {
    setStatus('idle');
    setError(null);
    setReportData(null);
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  const hasResults = status === 'complete';
  const printWindowLabel = params.mode === 'range'
    ? `${formatLongDate(params.startDate)} – ${formatLongDate(params.endDate)}`
    : formatReportDate(params.reportDate);

  return (
    <div
      className="fixed inset-0 z-50 overflow-hidden flex items-center justify-center p-4 print:p-0 print:overflow-visible print:static"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity print:hidden" />

      {/* Modal */}
      <div className="relative max-w-7xl w-full max-h-[95vh] bg-white rounded-lg shadow-xl flex flex-col print:max-w-none print:max-h-none print:rounded-none print:shadow-none">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between print:border-b-2">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{report.title}</h2>
            <p className="text-sm text-gray-500 mt-1">
              Sales Funnel (Lead → Visit → Sale) grouped by sales team — the last 5 working days,
              or a date range with weekly trend columns
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 print:hidden"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        {/* NOTE: no min-w-0 needed here. This pane is a flex item, so its
            min-width:auto would normally stretch it to the widest table — but
            overflow-y-auto makes it a scroll container, and a scroll
            container's automatic minimum size is zero. Measured in headless
            Chromium against the real compiled CSS at 1024/1280/1440: the week
            table's scroller reports scrollWidth 1498 vs clientWidth 942-1230
            and scrolls, with or without min-w-0. The reason a wide report
            looked un-scrollable was purely the INPUT affordance (overlay
            scrollbars + a mouse wheel can't scroll a horizontal-only
            container) — see the scroll controls in FunnelWeekLocationSection. */}
        <div className="flex-1 overflow-y-auto p-6 print:overflow-visible">
          <SalesTeamFunnelParamsForm
            params={params}
            onChange={handleParamsChange}
            onRun={handleRunReport}
            running={status === 'running'}
            validation={validation}
            runningMessage={runningMessage}
          />

          {/* Divider */}
          <div className="border-t border-gray-200 mb-6 print:hidden" />

          {/* Results Area */}
          <div id="report-print-content">
            {/* Print Header */}
            <div className="hidden print:block mb-4">
              <h1 className="text-xl font-bold">Sales Team Report</h1>
              <p className="text-sm text-gray-600">{printWindowLabel}</p>
            </div>

            {status === 'idle' && (
              <div className="text-center py-12">
                <DocumentChartBarIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">
                  Select locations and a report window, then click "Run Report" to generate results.
                </p>
              </div>
            )}

            {status === 'running' && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-600 mb-4" />
                <p className="text-gray-500">{runningMessage}</p>
              </div>
            )}

            {status === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700">{error || 'An error occurred while generating the report.'}</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                >
                  Try again
                </button>
              </div>
            )}

            {status === 'complete' && reportData && (
              <>
                {error && (
                  <div className="mb-4 px-3 py-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded print:hidden">
                    {error}
                  </div>
                )}
                <SalesTeamFunnelReport data={reportData.data as SalesTeamFunnelReportData} />
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between print:hidden">
          <ReportExportButtons
            onPrintPDF={handlePrintPDF}
            onExportExcel={handleExport}
            disabled={!hasResults}
            isExporting={isExporting}
          />
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
